import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Mora?", a: "Ansökan görs till tillståndsenheten i Mora kommun. Verksamhet i annan kommun i Dalarna (t.ex. Orsa, Rättvik, Leksand, Älvdalen) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Mora?", a: "Mora kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Mora?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-mora")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Mora" },
      ]}
      title="Serveringstillstånd Mora – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Mora: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.mora, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Mora?", body: "Tillståndsenheten i Mora kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Orsa, Rättvik, Leksand eller Älvdalen ansöker du hos respektive kommun." },
        { h2: "Krav från Mora kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Mora en stark säsongsvariation kring Vasaloppet, Siljan och sommarturismen. Kommunens riktlinjer för tillfälliga tillstånd och evenemangsservering är därför särskilt viktiga. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Mora", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7. Träna med Alkoholprovet i samma format." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Sök i god tid inför Vasaloppsveckan eller sommarsäsongen." },
        { h2: "Tillsyn och uppföljning", body: "Mora kommun bedriver aktiv tillsyn under evenemangsveckor tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – Vasaloppet och Siljan", body: "Mora präglas av vinterturism kring Vasaloppet och sommarturism vid Siljan. Många serveringsställen har hög belastning under några intensiva veckor per år – planera bemanning, ordning och tillsynsberedskap efter detta." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-falun", label: "Serveringstillstånd Falun" },
          { to: "/serveringstillstand-borlange", label: "Serveringstillstånd Borlänge" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-catering", label: "För catering" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Mora – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Mora: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Mora – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Mora." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-mora" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-mora" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Mora", url: "https://alkoholprovet.se/serveringstillstand-mora" },
      ]),
    ],
  }),
});
